import React, { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Flex, Text } from '@chilipiper/design-system'
import { Button, useShowToast } from '@chilipiper/design-system/src/new'
import { IconArrowLeft } from '@chilipiper/icons/figma-generated/src/icon'
import { IconGmail, IconOutlook } from '@chilipiper/icons/figma-generated/src/logo'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { useCalendarIntegration } from '@chilipiper/integrations'
import { OnboardingChiliPiperLogo, Progress } from '@chilipiper/onboarding'
import { captureException } from '@chilipiper/sentry'
import { routes } from '../../../routes/routes'
import { Link } from '../../../components/link/Link'
import { useFinishOnboarding } from '../../../api/workspace'
import { FinishingOnboarding } from '../components/FinishingOnboarding'
import { Spacer } from '../components/Spacer'
import { useIsDesktop } from '../useIsDesktop'
import { useOnboardingExtraSteps } from '../steps'
import { useConnectMailbox } from '@/api/integrations'
import type { MailboxProvider } from '@/api/integrations'
import { getExternalCalendar } from '@/components/personal-settings/integrations/CalendarIntegration/helpers'
import { mailboxCopy } from '@/components/personal-settings/integrations/MailboxIntegration/copy'
import { mailboxProviderSlug } from '@/components/personal-settings/integrations/MailboxIntegration/useMailboxIntegration'
import { useTrackFeature } from '@/hooks/useTracking'
import { ChilicalEvent, ChilicalFeature } from '@/tracking/events'
import { getMailboxReturnUrl, isMailboxReturn, withMailboxReturn } from '@/utils/mailbox-return'

const providerName: Record<MailboxProvider, string> = { google: 'Google', microsoft: 'Microsoft' }

// Last onboarding step (CEH-11738): the consent round trip lands back here with the return marker.
export const Email = () => {
  const isDesktop = useIsDesktop()
  const navigate = useNavigate()
  const track = useTrackFeature()
  const showToast = useShowToast()
  const extraSteps = useOnboardingExtraSteps()
  const [searchParams] = useSearchParams()
  const { userId } = useFireSessionContext()

  const { data: calendarIntegration, isError: isCalendarError } = useCalendarIntegration(userId)
  const primaryCalendar = calendarIntegration?.connectionStatuses[userId]?.primary
  const provider: MailboxProvider | undefined = !primaryCalendar
    ? undefined
    : getExternalCalendar(primaryCalendar) === 'MicrosoftOutlook'
      ? 'microsoft'
      : 'google'

  const { mutateAsync: finishOnboarding, isPending: isFinishing } = useFinishOnboarding()
  const { mutateAsync: connect, isPending: isConnecting } = useConnectMailbox(
    provider,
    getMailboxReturnUrl(window.location.href)
  )

  const complete = useCallback(
    async (allowed: boolean) => {
      try {
        await finishOnboarding()
      } catch (error) {
        captureException(new Error('Failed to finish onboarding', { cause: error }))
        showToast({ title: 'Failed to finish onboarding', variant: 'caution' })
        return
      }
      if (provider) {
        track(ChilicalEvent.OnboardingEmailStepCompleted, ChilicalFeature.Onboarding, {
          provider: mailboxProviderSlug[provider],
          allowed,
        })
      }
      track(ChilicalEvent.OnboardingFinished, ChilicalFeature.Onboarding)
      navigate(allowed ? withMailboxReturn(routes.meetingTypeList) : routes.meetingTypeList)
    },
    [finishOnboarding, navigate, provider, showToast, track]
  )

  const isReturning = isMailboxReturn(searchParams)
  const hasHandledReturn = useRef(false)
  useEffect(() => {
    if (!isReturning || hasHandledReturn.current || (!provider && !isCalendarError)) return
    hasHandledReturn.current = true
    void complete(true)
  }, [complete, isCalendarError, isReturning, provider])

  const handleAllow = async () => {
    try {
      const response = await connect()
      // Already connected: nothing to consent to, so the step is done.
      if (!('location' in response)) await complete(true)
    } catch (error) {
      captureException(new Error('Failed to start the mailbox connection', { cause: error }))
      showToast({ title: 'Could not start allowing email sending', variant: 'caution' })
    }
  }

  if (isFinishing || isReturning) return <FinishingOnboarding />

  const Icon = provider === 'microsoft' ? IconOutlook : IconGmail
  const totalSteps = 5 + extraSteps

  return (
    <Flex
      alignItems='center'
      w='100%'
      h='100%'
      flexDirection='column'
      bg='bg/minor'
      overflow='auto'
      px={6}
      py={5}
      position='relative'
    >
      <OnboardingChiliPiperLogo />
      <Spacer />
      <Flex flexDirection='column' alignItems='center' gap={4} mb={7}>
        <Text fontSize={[20, 20, 32]} fontWeight={600} color='fg/major' textAlign='center'>
          Send emails from your inbox?
        </Text>
        <Text
          textStyle={isDesktop ? 'body-monoline' : 'body-multiline-tight'}
          color='fg/moderate'
          textAlign='center'
        >
          Your team uses Flows that send emails as you. Allow it and they will come from your own
          inbox, so replies go straight to you.
        </Text>
      </Flex>

      <Flex flexDirection='column' maxWidth='528px' w='100%'>
        <Flex
          alignItems='center'
          gap={4}
          p={5}
          bg='bg/section'
          borderRadius={3}
          boxShadow='border/section'
          data-test-id='email-step-card'
        >
          <Icon width={40} height={40} />
          <Flex flexDirection='column' gap={1}>
            <Text textStyle='body-multiline' color='fg/major'>
              Sent emails also show up in your Sent folder.
            </Text>
            <Text textStyle='body-multiline' color='fg/moderate'>
              {provider ? providerName[provider] : 'Your provider'} only asks about email. You can
              change this anytime in Integrations.
            </Text>
          </Flex>
        </Flex>

        <Flex justifyContent='space-between' mt={7} mb={9} gap={4}>
          <Link to={routes.onboarding.extension}>
            {linkProps => (
              <Button
                variant='minor'
                startContent={<Button.StartContent variant='icon' icon={IconArrowLeft} />}
                as='a'
                {...linkProps}
              >
                Back
              </Button>
            )}
          </Link>
          <Flex gap={4}>
            <Button
              variant='moderate'
              onClick={() => void complete(false)}
              data-test-id='email-skip-button'
            >
              Skip for now
            </Button>
            <Button
              variant='major'
              onClick={() => void handleAllow()}
              isDisabled={!provider || isConnecting}
              data-test-id='email-allow-button'
            >
              {isConnecting ? mailboxCopy.connecting : mailboxCopy.allow}
            </Button>
          </Flex>
        </Flex>
        <Progress totalSteps={totalSteps} currentStep={totalSteps} />
      </Flex>
      <Spacer />
    </Flex>
  )
}
